import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  PiggyBank, ChevronDown, ChevronUp, Clock, Car,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AccessRestricted } from "@/components/access-restricted";
import { savingsCategoryRows, type ApiSavingsSummary } from "@/lib/savingsSummary";
import { liveDaysUntil } from "@/lib/liveDays";

interface LedgerRow {
  costCategory: string;
  affectedEntityId: string;
  periodEnd: string | null;
  amountLowCents: number;
  amountHighCents: number;
  sourceQuality: string;
  excludedFromSummary: boolean;
  exclusionReason: string | null;
}

interface Finding {
  opportunityId: string;
  opportunityType: string;
  scopeKey: string;
  severity: string;
  confidence: string;
  estimatedSavingsLowCents: number;
  estimatedSavingsHighCents: number;
  templateExplanation: string | null;
  calculation: any;
  evidence: any;
  ledger: LedgerRow[];
  eligibleLowCents: number;
  eligibleHighCents: number;
  excludedLowCents: number;
  excludedCount: number;
}

interface Overview {
  enabled: boolean;
  lastRun: { status: string; finishedAt: string | null } | null;
  refreshing: boolean;
  summary: ApiSavingsSummary | null;
  suppressions: Array<{ opportunityType: string; reason: string; detail?: string }>;
  findings: Finding[];
  effectiveSettings: {
    minimumSavingsThresholdCents: number;
    origins: Record<string, string>;
  };
}

function fmtMoney(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default function PotentialSavingsPage() {
  const { t, i18n } = useTranslation(["savings", "common"]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  // While a background analysis run is in flight (triggered automatically by
  // statement uploads/deletes), poll so the page updates when it finishes.
  const { data, isLoading } = useQuery<Overview>({
    queryKey: ["/api/savings/overview"],
    refetchInterval: (q) => (q.state.data?.refreshing ? 5_000 : false),
  });

  if (user?.memberPermissions && !user.memberPermissions.financialAccess) {
    return <AccessRestricted feature={t("savings:page.title")} />;
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-[100dvh] bg-background p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-4xl space-y-5">
          <Skeleton className="h-5 w-28 rounded-full" />
          <Skeleton className="h-11 w-72" />
          <Skeleton className="h-44 w-full rounded-2xl" />
          <Skeleton className="h-28 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!data.enabled) {
    return (
      <div className="min-h-[100dvh] bg-background p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-4xl">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">{t("savings:page.title")}</p>
          <h1 className="text-3xl font-semibold tracking-tight">{t("savings:page.title")}</h1>
          <Card className="mt-8 rounded-2xl border-card-border/70 shadow-md shadow-primary/5">
            <CardContent className="pt-7">
            <p className="font-medium">{t("savings:disabled.title")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("savings:disabled.body")}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const s = data.summary;
  const visibleFindings = data.findings;
  const categoryRows = savingsCategoryRows(s);
  const belowThreshold = data.suppressions.filter((x) => x.reason === "savings_below_threshold");
  const settings = data.effectiveSettings;
  // Calm, plain-language freshness note derived from the latest done run:
  // "Updated today at 6:04 AM" / "Updated yesterday at …" / "Updated Aug 1 at …".
  const freshnessNote = (() => {
    const raw = data.lastRun?.finishedAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    const time = d.toLocaleTimeString(i18n.language, { hour: "numeric", minute: "2-digit" });
    const now = new Date();
    const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
    if (dayDiff === 0) return t("savings:headline.updatedToday", { time });
    if (dayDiff === 1) return t("savings:headline.updatedYesterday", { time });
    const date = d.toLocaleDateString(i18n.language, {
      month: "short", day: "numeric",
      ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
    });
    return t("savings:headline.updatedOn", { date, time });
  })();

  return (
    <div className="min-h-[100dvh] bg-background/70 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-4xl space-y-7">
      <div className="border-b border-border/70 pb-6">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/80">{t("savings:page.title")}</p>
        <h1 className="text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{t("savings:page.title")}</h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground">{t("savings:page.subtitle")}</p>
      </div>

      {/* Headline */}
      <Card data-testid="card-savings-headline" className="relative overflow-hidden rounded-2xl border-primary/15 bg-gradient-to-br from-primary/[0.08] via-card to-card shadow-lg shadow-primary/5">
        <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full border-[22px] border-primary/[0.07]" />
        <CardContent className="relative pt-7 sm:pt-8">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/12 ring-1 ring-primary/15">
              <PiggyBank className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("savings:headline.title")}</p>
              {categoryRows.length === 0 ? (
                <p className="mt-2 text-4xl font-semibold tracking-[-0.04em]" data-testid="text-savings-total">
                  {fmtMoney(0)}
                </p>
              ) : (
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  {categoryRows.map((row) => (
                    <div key={row.key} data-testid={`row-savings-${row.key}`} className="border-l-2 border-primary/25 pl-3">
                      <p className="text-2xl font-semibold tracking-[-0.03em] leading-tight">
                        {row.lowCents === row.highCents
                          ? fmtMoney(row.lowCents)
                          : t("savings:headline.range", { low: fmtMoney(row.lowCents), high: fmtMoney(row.highCents) })}
                      </p>
                      <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                        {t(`savings:headline.category.${row.key}`)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-primary/70" />
                  <span data-testid="text-savings-freshness">
                    {freshnessNote ?? t("savings:headline.neverAnalyzed")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Findings or empty state */}
      {visibleFindings.length === 0 ? (
        <Card data-testid="card-savings-empty" className="rounded-2xl border-dashed bg-card/60">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <PiggyBank className="h-6 w-6" />
            </div>
            <p className="font-semibold">{t("savings:empty.title")}</p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">{t("savings:empty.body")}</p>
            {belowThreshold.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3">
                {t("savings:empty.suppressedNote", {
                  count: belowThreshold.length,
                  threshold: fmtMoney(settings.minimumSavingsThresholdCents),
                })}
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        visibleFindings.map((f) => (
          <FindingCard
            key={f.opportunityId}
            finding={f}
            expanded={expanded === f.opportunityId}
            onToggle={() => setExpanded(expanded === f.opportunityId ? null : f.opportunityId)}
          />
        ))
      )}

      </div>
    </div>
  );
}

function DetailItem({ label, value, origin }: { label: string; value: string; origin: string }) {
  return (
    <div className="min-h-[82px] bg-card/55 px-3.5 py-3.5 transition-colors hover:bg-primary/[0.04] sm:px-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">{label}</p>
      <p className="mt-2 text-sm font-semibold tracking-tight">{value}</p>
      {origin && <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{origin}</p>}
    </div>
  );
}

function FindingCard({
  finding: f, expanded, onToggle,
}: {
  finding: Finding; expanded: boolean; onToggle: () => void;
}) {
  const { t, i18n } = useTranslation(["savings", "common"]);
  const isCurtailment = f.opportunityType === "curtailment_exposure";
  const vin = isCurtailment ? f.scopeKey.split(":").pop() : null;
  const ev = Array.isArray(f.evidence) ? f.evidence[0] : f.evidence;
  const vehicleLabel = isCurtailment
    ? [ev?.year, ev?.make, ev?.model].filter(Boolean).join(" ")
    : "";
  const vehicleDisplay = vehicleLabel ? `${vehicleLabel} — ${vin ?? ""}` : (vin ?? "");
  const rawBoundary = isCurtailment
    ? (Array.isArray(f.evidence) ? f.evidence[0]?.boundary : f.evidence?.boundary)
    : null;
  const boundaryPeriod: 1 | 2 | 3 | null =
    rawBoundary === 1 || rawBoundary === 2 || rawBoundary === 3 ? rawBoundary : null;
  const deadline = (() => {
    if (!isCurtailment) return null;
    const raw = f.ledger.find((r) => r.periodEnd)?.periodEnd;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d;
  })();
  // Format from UTC components: periodEnd is stored as UTC midnight, so local
  // formatting would show the previous day for viewers west of UTC.
  const dueDate = deadline
    ? deadline.toLocaleDateString(i18n.language, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
    : null;
  // Live day count: computed from the persisted deadline DATE at render time
  // so it ticks down daily even between analysis runs (UTC day boundary, in
  // sync with the daily server refresh). Falls back to the stored number.
  const liveDays = liveDaysUntil(deadline, f.calculation?.inputs?.daysUntil);
  const title = isCurtailment
    ? t("savings:finding.curtailment_exposure.title", { vin: vehicleDisplay })
    : t("savings:finding.generic.title");
  const desc = isCurtailment
    ? t("savings:finding.curtailment_exposure.desc")
    : (f.templateExplanation ?? "");

  return (
    <Card data-testid={`card-finding-${f.opportunityType}`} className="group rounded-xl border-border/70 bg-card transition-colors duration-200 hover:border-primary/35 hover:bg-primary/[0.02]">
      <CardContent className="p-0">
        <div className="flex min-h-[88px] items-stretch">
          <div className="flex w-2 shrink-0 bg-primary/75" />
          <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 px-4 py-4 sm:grid-cols-[minmax(0,1.7fr)_minmax(130px,0.8fr)_auto] sm:px-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Car className="h-3.5 w-3.5 shrink-0 text-primary" />
                <p className="truncate text-sm font-semibold tracking-tight">{title}</p>
              </div>
              {typeof ev?.lenderName === "string" && ev.lenderName && (
                <p className="mt-1 truncate pl-5 text-xs text-muted-foreground" data-testid="text-finding-lender">{ev.lenderName}</p>
              )}
            </div>
            <div className="row-span-2 text-right sm:row-span-1 sm:text-left">
              {rawBoundary !== null && liveDays !== null ? (
                <>
                  <p className="text-sm font-semibold text-primary">{t("savings:detail.boundary", { days: liveDays })}</p>
                  {dueDate && <p className="mt-0.5 text-xs text-muted-foreground">{dueDate}</p>}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">{desc}</p>
              )}
            </div>
            <div className="text-right sm:row-span-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("savings:detail.fee")}</p>
              <p className="mt-0.5 text-lg font-semibold tracking-[-0.03em] text-primary" data-testid={`text-finding-amount-${f.opportunityType}`}>
                {f.eligibleLowCents === f.eligibleHighCents
                  ? fmtMoney(f.eligibleLowCents)
                  : t("savings:finding.range", { low: fmtMoney(f.eligibleLowCents), high: fmtMoney(f.eligibleHighCents) })}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              data-testid={`button-toggle-detail-${f.opportunityType}`}
              className="col-span-2 -ml-3 h-7 w-fit justify-start gap-1 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary sm:col-span-3"
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {expanded ? t("savings:finding.hideDetail") : t("savings:finding.showDetail")}
            </Button>
          </div>
        </div>

        {expanded && isCurtailment && f.calculation?.inputs && (
          <div className="mx-4 mb-4 overflow-hidden rounded-lg border border-border/70 bg-muted/15 sm:mx-5 sm:mb-5">
            <div className="grid grid-cols-2 divide-x divide-y divide-border/60 sm:grid-cols-3 sm:divide-y-0">
            <DetailItem
              label={t("savings:detail.principal")}
              value={fmtMoney(f.calculation.inputs.principalCents ?? 0)}
              origin=""
            />
            <DetailItem
              label={t("savings:detail.rate")}
              value={`${Number(f.calculation.inputs.aprPct ?? 0).toFixed(2)}%`}
              origin=""
            />
            <DetailItem
              label={t("savings:detail.boundary", { days: liveDays ?? f.calculation.inputs.daysUntil ?? 0 })}
              value={`${f.calculation.inputs.daysHeld ?? 0} / ${f.calculation.inputs.boundaryDays ?? 0} ${t("common:days", { defaultValue: "days" })}`}
              origin=""
            />
            {dueDate && (
              <DetailItem
                label={t("savings:detail.dueDate")}
                value={dueDate}
                origin=""
              />
            )}
            {(f.calculation.results?.futureInterestCents ?? 0) > 0 && (
              <DetailItem
                label={t("savings:detail.futureInterest")}
                value={fmtMoney(f.calculation.results?.futureInterestCents ?? 0)}
                origin={t("savings:detail.notCounted")}
              />
            )}
            <DetailItem
              label={
                boundaryPeriod
                  ? t(`savings:detail.feePeriod.${boundaryPeriod}`)
                  : t("savings:detail.fee")
              }
              value={fmtMoney(f.calculation.results?.curtailmentFeeCents ?? 0)}
              origin=""
            />
            {(f.calculation.results?.principalPaydownCents ?? 0) > 0 && (
              <DetailItem
                label={t("savings:detail.paydown")}
                value={fmtMoney(f.calculation.results?.principalPaydownCents ?? 0)}
                origin={t("savings:detail.paydownNote")}
              />
            )}
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
